import { Section } from '@/components/containers/section';
import { useTranslation } from '@/contexts/i18n-context';
import { useLoggedUser } from '@/contexts/current-user-context';
import { useGetPatient } from '@/api/get-personal-info';
import { Loader } from '@/components/loaders/loader';
import { InfoRow } from '@/components/encounter/info-row';
import { formatPhoneNumber, getAge } from '@/util/helpers';
import { useReferenceData } from '@/contexts/reference-data-context';
import { useEncounter } from '@/contexts/encounter-context';
import { InsuranceInfoSection } from './insurance-info-section';
import styles from './patient-info-section.module.css';
import reviewStyles from '@/styles/review-page.module.css';
import { Gender, ImageSize } from '@/api/api.schemas';
import { useGetPatientAvatar } from '@/api/get-patient-avatar';
import { PaymentSection } from './payment-section';
import { PatientImageSvg } from '../svgs/patient-image';
import { formatDate } from '@/util/formats';

const translationBase = 'visit.review';

interface PatientInfoSectionProps {
  showEditButton?: boolean;
  showPaymentSection: boolean;
}

export function PatientInfoSection({
  showEditButton = true,
  showPaymentSection,
}: PatientInfoSectionProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();

  return (
    <Section
      className={styles.section}
      title={t(`${translationBase}.patient.title`)}
      dataTestId="review-section-patient"
    >
      <PatientInfoSectionContent />
      <InsuranceInfoSection encounterId={encounter.id} showEditButton={showEditButton} />
      {showPaymentSection && <PaymentSection />}
    </Section>
  );
}

export function PatientInfoSectionContent() {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { encounter } = useEncounter();
  const { data: patient, isFetching, error } = useGetPatient(user.id);
  const { data: patientAvatar, isFetching: isFetchingPatientAvatar } = useGetPatientAvatar({
    size: ImageSize.Thumb,
  });
  const { getStateName } = useReferenceData();

  const getGenderDisplay = (gender?: Gender | null) => {
    if (!gender) return '';

    if (gender === Gender.other) {
      return patient?.genderOther ?? t('common.gender.other');
    }
    return t(`common.gender.${gender}`);
  };

  const getAgeDisplay = (dateOfBirth?: string | null) => {
    if (!dateOfBirth) return '';

    const age = getAge(dateOfBirth);
    if (!age) return '';
    return `${age} ${age === 1 ? t(`${translationBase}.patient.yearOld`) : t(`${translationBase}.patient.yearsOld`)}`;
  };

  return (
    <>
      {isFetching && <Loader type="small" />}
      {error && <p>{t(`${translationBase}.patient.error`)}</p>}
      {patient && (
        <div className={reviewStyles.detailsContainer}>
          {isFetchingPatientAvatar ? (
            <Loader type="small" className={reviewStyles.photo} />
          ) : patientAvatar ? (
            <img
              src={URL.createObjectURL(patientAvatar)}
              alt={t('svg.patientAvatar')}
              className={reviewStyles.photo}
            />
          ) : (
            <PatientImageSvg className={reviewStyles.photo} />
          )}
          <div className={styles.patientDetails}>
            <div className={styles.patientName}>
              <h3 className="h4">{`${patient.firstName} ${patient.middleName ?? ''} ${patient.lastName}`}</h3>
              <p className={styles.subtext}>
                {[getGenderDisplay(patient.gender), getAgeDisplay(patient.dateOfBirth)]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </div>
            <InfoRow
              icon="calendar_today"
              title={t(`${translationBase}.patient.birthday`)}
              details={formatDate(patient.dateOfBirth)}
            />
            <InfoRow
              icon="location_on"
              title={t(`${translationBase}.patient.address`)}
              details={[
                patient.mailingAddress1,
                patient.mailingAddress2,
                patient.city,
                patient.state,
                patient.zip,
              ]
                .filter(Boolean)
                .join(', ')}
            />
            <InfoRow
              icon="call"
              title={t(`${translationBase}.patient.phone`)}
              details={
                formatPhoneNumber(patient.cellPhone ?? patient.homePhone) ??
                t(`${translationBase}.na`)
              }
            />
            <InfoRow
              icon="mail"
              title={t(`${translationBase}.patient.email`)}
              details={<p className={styles.email}>{patient.email}</p>}
            />
            <InfoRow
              icon="distance"
              title={t(`${translationBase}.patient.location`)}
              details={getStateName(encounter.state)}
              detailsTestId="review-patient-location-value"
            />
          </div>
        </div>
      )}
    </>
  );
}
